import * as React from "react";

import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Button, FormGroup, InputGroup, FormControl, Popover, OverlayTrigger } from "react-bootstrap";
import { McwAmountChooserInfoPanel } from "betty-ordercapture-amountchooser";
import { isTouchScreen } from "shared/src/utils/isTouchScreen";
import { ShoppingCartPosition } from "shared/src/types/ShoppingCartPosition";
import { connect } from "shared/src/stores/storeConnect";
import { PriceConfigStore } from "shared/src/stores/PriceConfigStore";
import { PriceConfig } from "shared/src/types/articlesearch/Price";
import { getPositionDisplayPrice, primaryIsNet } from "../../../utils/PriceDisplayFramework";
import { getDerivedPricePromotionInfo, getDnrLabels } from "shared/src/utils/PriceDataHelper";
import {
    BulkDiscountSourceLevel,
    SellingPriceDnrInfo,
    PriceAndPromotionInfo
} from "shared/src/types/PriceAndPromotionInfo";

import { getComponentByName } from "shared/src/components/LazyWrapper";
import { isEmpty, isNumber } from "shared/src/utils/util";

const BulkDiscountTiersDisplay = getComponentByName("sd_pdp_ui/BulkDiscountTiersDisplay");

type AmountChooserProps = {
    amount: number | undefined | null;
    amountInBasket: number | undefined | null;
    priceInBasket: ShoppingCartPosition | undefined | null;
    isWeightArticle: boolean;
    weightPerPiece:
        | {
              unit: string;
              value: number;
          }
        | undefined
        | null;
    dnrInfo: SellingPriceDnrInfo | undefined | null;
    disabled: boolean;
    tabIndex?: number;
    idForBasket: string;
    showWeightInformation: boolean;
    minOrderQuantity?: number;
    onChange: (amount: number | undefined | null) => void;
    onEnter: () => void;
    priceConfig: PriceConfig;
    priceAndPromotionInfo: PriceAndPromotionInfo;
};

type State = {
    showInfoPopover: boolean;
};

export class DisconnectedAmountChooser extends React.Component<AmountChooserProps, State> {
    static contextTypes = {
        intl: PropTypes.object.isRequired
    };

    state: State = { showInfoPopover: false };

    parseAmount(amount: string) {
        if (this.props.isWeightArticle && !this.props.weightPerPiece) {
            return parseFloat(amount.replace(",", ".")) || 0.0;
        }
        return parseInt(amount, 10) || 0;
    }

    renderInfoPanel() {
        if (!isNumber(this.props.amountInBasket) || !this.props.weightPerPiece) {
            return null;
        }
        const totalPrice = getPositionDisplayPrice(this.props.priceInBasket?.prices, this.props.priceConfig);
        return (
            <div className="mfcss_amountchooser-infopanel--absoluteposition">
                <McwAmountChooserInfoPanel
                    timeoutStandard={5000}
                    totalWeight={this.props.amountInBasket}
                    weightPerPieceValue={(this.props.weightPerPiece || {}).value}
                    unitOfMeasurement={(this.props.weightPerPiece || {}).unit}
                    totalPrice={totalPrice}
                    currency={this.props.priceInBasket?.prices?.currency}
                    showPermanently={this.state.showInfoPopover || this.props.showWeightInformation}
                />
            </div>
        );
    }

    wrapInDnrPopover(element: React.ReactNode) {
        const dnrInfo = this.props.priceAndPromotionInfo.summaryDnrInfo;
        if (!dnrInfo) {
            return element;
        }

        const dnrLabels: Array<string> = getDnrLabels(this.props.priceAndPromotionInfo);
        const derivedDnrLevels: Array<BulkDiscountSourceLevel> = getDerivedPricePromotionInfo(
            this.props.priceAndPromotionInfo
        );
        // FIXME(KiHu): This is only necessary because of naming incosistencies between explore-ui and pdp-ui.
        const weightPerPiece = this.props.weightPerPiece
            ? {
                  value: this.props.weightPerPiece.value,
                  uom: this.props.weightPerPiece.unit
              }
            : null;
        const bulkDiscountTable = BulkDiscountTiersDisplay({
            summaryDnrLevels: derivedDnrLevels,
            currency: this.props.priceAndPromotionInfo?.currency,
            amountInCart: this.props.amountInBasket,
            isWeightedArticle: this.props.isWeightArticle,
            weightPerPiece: weightPerPiece,
            useNetPrice: primaryIsNet(this.props.priceConfig),
            dnrLabels: dnrLabels
        });
        if (bulkDiscountTable) {
            const dnrTrigger = !isEmpty(dnrInfo) ? ["focus", "hover"] : [];
            const overlay = <Popover id={this.props.idForBasket}>{bulkDiscountTable}</Popover>;

            return (
                <OverlayTrigger delayHide={100} overlay={overlay} placement="top" trigger={dnrTrigger}>
                    {element}
                </OverlayTrigger>
            );
        }
        return element;
    }

    handleAmountChange(event: React.SyntheticEvent<any>) {
        event.preventDefault();
        const value = (event.target as any).value;
        if (value === "") {
            this.props.onChange(null);
        } else {
            this.props.onChange(Math.max(this.parseAmount((event.target as any).value), 0));
        }
    }

    handleItemAmountIncrease() {
        this.props.onChange((this.props.amount || 0) + 1);
    }

    handleItemAmountDecrease() {
        if (this.props.amount && this.props.amount >= 1) {
            this.props.onChange(this.props.amount - 1);
        }
    }

    showInfoPopover(event: any) {
        this.setState({
            showInfoPopover: true
        });
        event.target.select(); // Preselect the input value, so that typing resets the old one.
    }

    resetState() {
        this.setState({
            showInfoPopover: false
        });
        if (!this.props.amount) {
            this.props.onChange(1);
        }
    }

    handleKeyPress(event: any) {
        if (event.key === "Enter") {
            this.props.onEnter();
        }
    }

    render() {
        const displayAsWeightedArticle = this.props.isWeightArticle && !this.props.weightPerPiece;
        const weightAddon = displayAsWeightedArticle ? (
            <span className="form-control-addon">
                <FormattedMessage id="EX_ARTICLE_KG" />
            </span>
        ) : null;
        const touch = isTouchScreen();
        return (
            <div className={"mfcss_card-article-2--actions-amount"}>
                {this.wrapInDnrPopover(
                    <div className={"mfcss_amountchooser"}>
                        <FormGroup className={weightAddon ? "has-addon" : ""}>
                            <InputGroup>
                                {touch && (
                                    <InputGroup.Button>
                                        <Button
                                            bsStyle="link"
                                            className="sd-amount-chooser-decrease btn-square"
                                            aria-label={this.context.intl.formatMessage({
                                                id: "SD_DECREASE_BASKET_BUTTON_ARIA_LABEL"
                                            })}
                                            onClick={() => this.handleItemAmountDecrease()}>
                                            <span className="icon-minus icon-lg" />
                                        </Button>
                                    </InputGroup.Button>
                                )}
                                <FormControl
                                    value={this.props.amount!}
                                    type="number"
                                    disabled={this.props.disabled}
                                    tabIndex={this.props.tabIndex}
                                    onFocus={(event) => this.showInfoPopover(event)}
                                    onBlur={() => this.resetState()}
                                    onKeyPress={(event) => this.handleKeyPress(event)}
                                    onChange={(event) => this.handleAmountChange(event)}
                                    aria-label={this.context.intl.formatMessage({
                                        id: "SD_BASKET_AMOUNT_INPUT_ARIA_LABEL"
                                    })}
                                />
                                {weightAddon}
                                {touch && (
                                    <InputGroup.Button>
                                        <Button
                                            bsStyle="link"
                                            className="sd-amount-chooser-increase btn-square"
                                            aria-label={this.context.intl.formatMessage({
                                                id: "SD_INCREASE_BASKET_BUTTON_ARIA_LABEL"
                                            })}
                                            onClick={() => this.handleItemAmountIncrease()}>
                                            <span className="icon-plus icon-lg" />
                                        </Button>
                                    </InputGroup.Button>
                                )}
                            </InputGroup>
                        </FormGroup>
                    </div>
                )}
                {this.props.weightPerPiece && this.renderInfoPanel()}
            </div>
        );
    }
}

export const AmountChooser = connect(
    {
        stores: [[PriceConfigStore, (store: typeof PriceConfigStore) => ({ priceConfig: store.getConfig() })]]
    },
    DisconnectedAmountChooser,
    false,
    "AmountChooser"
);
